import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiPreferencesTab } from '@/core/AiPreferencesTab';
import { SaveDatasourcePreferencesTab } from '@/core/SaveDatasourcePreferencesTab';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { DATE_FORMAT_PREFERENCES, getDateOrderingPattern } from '@/datetime/dateFormatPreference.utilities';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { EditUserProfile } from '@/header/EditUserProfile.atom';
import { saveLanguage, saveSystemPreference } from '@/header/systemPreferences.actions';
import { useUnsavedChangesGuard } from '@/header/useUnsavedChangesGuard.hook';
import { setDatasources } from '@/search/search.actions';
import { genAIEnabled } from '@/services/systemConfiguration.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import { LOCALE_DATA } from '@/utilities/i18n.constants';
import {
  set24HourFormat,
  setDarkMode,
  setDateFormat,
  setPreferNewTab,
  setUserPreferencesDisplay,
  setUserPreferencesTab,
  setUserTimeZone,
} from '@/workbench/workbench.actions';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';
import { Modal, Tabs } from '@seeqdev/qomponents';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

interface EditUserPreferencesProps {
  closeFn: () => void;
}

// IconSelect translates the option text
const LANGUAGE_OPTIONS = LOCALE_DATA.map((language) => ({
  text: language.text,
  value: language.key,
}));

const DATE_FORMAT_OPTIONS = DATE_FORMAT_PREFERENCES.map((preference) => ({
  text: preference === 'BROWSER' ? 'USER.DATE_FORMAT_OPTIONS.BROWSER' : getDateOrderingPattern(preference),
  value: preference,
}));

export const EditUserPreferencesModal = ({ closeFn }: EditUserPreferencesProps) => {
  const { t } = useTranslation();

  const { userTimeZone, userLanguage, preferNewTab, darkMode, use24HourFormat, dateFormat } = sqWorkbenchStore;

  const { userPreferencesTab } = useFlux(sqWorkbenchStore);
  const isTrusted = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.isTrusted);
  const showAiPreferencesTab = genAIEnabled() && isTrusted;
  const currentUserMissingInfo = sqWorkbenchStore.isCurrentUserMissingInfo();
  // A new user must choose a time zone and complete their profile before they can close the modal. Decided once when
  // it opens, because each choice is saved as it is made and would otherwise end setup part way through.
  const [isNewUserSetup] = useState(() => sqWorkbenchStore.isUserSetupIncomplete());

  const visibleTabIds = new Set([
    PREFERENCE_TABS.PROFILE,
    PREFERENCE_TABS.SYSTEM,
    ...(isTrusted ? [PREFERENCE_TABS.DATASOURCES] : []),
    ...(showAiPreferencesTab ? [PREFERENCE_TABS.AI] : []),
  ]);
  const safeActiveTab = visibleTabIds.has(userPreferencesTab)
    ? userPreferencesTab
    : currentUserMissingInfo
      ? PREFERENCE_TABS.PROFILE
      : PREFERENCE_TABS.SYSTEM;

  const selectedLanguage = LANGUAGE_OPTIONS.find((option) => option.value === userLanguage);
  const selectedDateFormat = DATE_FORMAT_OPTIONS.find((option) => option.value === dateFormat);

  useEffect(() => {
    setDatasources('main');
  }, []);

  const { setHasUnsavedChanges, guard, unsavedChangesModal } = useUnsavedChangesGuard({
    messageKey: 'USER.UNSAVED_TAB_CHANGES',
  });

  // The one place that decides where a user goes after saving a setup step: on to whichever step is left, or out of
  // the modal once setup is done. It reads the store when called because the step that called it has just saved.
  const advanceSetup = (): void => {
    if (!sqWorkbenchStore.userTimeZone) {
      setUserPreferencesTab(PREFERENCE_TABS.SYSTEM);
    } else if (sqWorkbenchStore.isCurrentUserMissingInfo()) {
      setUserPreferencesTab(PREFERENCE_TABS.PROFILE);
    } else {
      closeFn();
    }
  };

  // Every preference on the System tab is saved as soon as it changes, so its fields show the saved values. During setup
  // the tab also has a button that accepts the displayed time zone (saving the default one if the user kept it).
  const finishTimeZoneStep = (): void => {
    if (!userTimeZone) {
      saveSystemPreference('timezone', () => setUserTimeZone(sqTimezones.defaultTimezone));
    }
    advanceSetup();
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'userPreferences',
      components: [
        {
          component: 'TimeZoneSelectorFormComponent',
          name: 'timezone',
          label: 'USER.TIME_ZONE',
          value: userTimeZone || sqTimezones.defaultTimezone,
          onChange: (timezone) => {
            if (timezone) {
              saveSystemPreference('timezone', () => setUserTimeZone(timezone));
            }
          },
          defaultTimeZone: sqTimezones.defaultTimezone,
          testId: 'timezone',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'use24HourFormat',
          name: 'use24HourFormat',
          label: '',
          onChange: (use24Hours) => saveSystemPreference('use24HourFormat', () => set24HourFormat(use24Hours)),
          checkboxLabel: 'USER.USE_24_HOUR_FORMAT',
          value: use24HourFormat,
        },
        {
          component: 'IconSelectFormComponent',
          name: 'dateFormat',
          label: 'USER.DATE_FORMAT',
          value: selectedDateFormat,
          onChange: (option) => saveSystemPreference('dateFormat', () => setDateFormat(option.value)),
          selectOptions: DATE_FORMAT_OPTIONS,
          insideModal: true,
          testId: 'dateFormat',
        },
        {
          component: 'IconSelectFormComponent',
          name: 'language',
          label: 'USER.LANGUAGE.SELECTION',
          value: selectedLanguage,
          onChange: (option) => void saveLanguage(option.value),
          selectOptions: LANGUAGE_OPTIONS,
          insideModal: true,
          testId: 'language',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'userPreferNewTab',
          name: 'preferNewTab',
          label: '',
          onChange: (openInNewTab) => saveSystemPreference('preferNewTab', () => setPreferNewTab(openInNewTab)),
          checkboxLabel: 'USER.PREFER_NEW_TAB',
          value: preferNewTab,
        },
        {
          component: 'CheckboxFormComponent',
          id: 'userDarkMode',
          name: 'userDarkMode',
          label: '',
          // WorkbenchWrapper applies the theme whenever the saved dark mode preference changes
          onChange: (useDarkMode) => saveSystemPreference('darkMode', () => setDarkMode(useDarkMode)),
          checkboxLabel: 'DARK_MODE.ENABLE',
          value: darkMode,
        },
      ],
    },
  ];

  return (
    <Modal
      open={true}
      onSubmit={_.noop}
      onClose={closeFn}
      data-testid="editUserPreferences"
      hideCloseIcon={isNewUserSetup}
      title={t('EDIT_PREFERENCES')}
      hideFooterButtons={true}>
      <Tabs
        testId="userPreferencesTabs"
        extraClassNames="width-maximum"
        activeTab={safeActiveTab}
        defaultActiveTab={currentUserMissingInfo ? PREFERENCE_TABS.PROFILE : PREFERENCE_TABS.SYSTEM}
        onTabSelect={(tabId) => guard(() => setUserPreferencesTab(tabId))}
        tabs={[
          {
            id: PREFERENCE_TABS.PROFILE,
            testId: 'userProfilePreferencesTab',
            label: t('USER.PROFILE_PREFERENCES'),
            tabContentExtraClassNames: 'link-no-underline tw:h-full',
            tabExtraClassNames: 'link-no-underline',
            content: (
              <div className="tw:pt-sq-10" data-testid="editUserPreferences">
                <EditUserProfile
                  closeFn={() => {
                    setUserPreferencesDisplay(false);
                    closeFn();
                  }}
                  onSaved={advanceSetup}
                  onUnsavedChangesChange={setHasUnsavedChanges}
                />
              </div>
            ),
          },
          {
            id: PREFERENCE_TABS.SYSTEM,
            label: t('USER.SYSTEM_PREFERENCES'),
            tabContentExtraClassNames: 'link-no-underline tw:h-full',
            tabExtraClassNames: 'link-no-underline',
            testId: 'systemPreferencesTab',
            content: (
              <div className="tw:pt-sq-10" data-testid="editUserPreferences">
                <SimpleSaveFormBuilder
                  formDefinition={formDefinition}
                  submitFn={finishTimeZoneStep}
                  submitBtnLabel={currentUserMissingInfo ? 'NEXT' : 'SAVE'}
                  closeFn={closeFn}
                  hideSubmit={!isNewUserSetup}
                  hideCancel={true}
                />
              </div>
            ),
          },
          ...(isTrusted
            ? [
                {
                  id: PREFERENCE_TABS.DATASOURCES,
                  label: t('USER.DATASOURCE_PREFERENCES'),
                  tabContentExtraClassNames: 'link-no-underline  tw:h-full',
                  tabExtraClassNames: 'link-no-underline',
                  testId: 'userDatasourcePreferencesTab',
                  content: (
                    <div className="tw:pt-sq-10" data-testid="editUserDatasourcePreferences">
                      <SaveDatasourcePreferencesTab />
                    </div>
                  ),
                },
              ]
            : []),
          ...(showAiPreferencesTab
            ? [
                // AI Preferences Tab
                {
                  id: PREFERENCE_TABS.AI,
                  label: t('USER.AI_PREFERENCES'),
                  testId: 'aiPreferencesTab',
                  tabContentExtraClassNames: 'link-no-underline  tw:h-full',
                  content: (
                    <div className="tw:pt-sq-10" data-testid="editUserAiPreferences">
                      <AiPreferencesTab />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      {unsavedChangesModal}
    </Modal>
  );
};
